import React, {
    memo,
    useCallback,
    useImperativeHandle,
    useMemo,
    useState,
} from "react";

import { useCompoundFormState } from "@impero-com/common/utils/form/formAtom/hooks";
import {
    FormAtom,
    ValidationResult,
} from "@impero-com/common/utils/form/formAtom/types";
import { SplitFormAtomAction } from "@impero-com/common/utils/form/splitFormAtom";
import { assertNever, set } from "@impero-com/common/utils/obj";
import { atom as createAtom, useAtom, useAtomValue } from "jotai";
import { useAtomCallback } from "jotai/utils";

import { SavedBlob } from "api/generated/model";
import {
    ButtonsBehaviour,
    ElementsAndSectionsBehaviour,
} from "components/elementDesigner/behaviours";
import { ElementDesigner } from "components/elementDesigner/elementDesigner";
import {
    ElementButtonSpec,
    GridLocation,
    SectionSpecId,
} from "components/elementDesigner/types";
import { Field } from "components/fieldBuilder/models";
import { insertableSplitFormAtom } from "utils/form/insertableSplitFormAtom";

import { DrawerBehavior, FieldDrawer, FieldDrawerType } from "./fieldDrawer";
import { useCopyBlobsMutation } from "./queries/useCopyBlobsMutation";
import * as theme from "./theme.css";

export interface CompoundFormRef {
    validate: (force: boolean) => Promise<ValidationResult<unknown>>;
}
export type CompoundFormForwardedRef = React.ForwardedRef<CompoundFormRef>;

// delay to allow drawer to close before field is deleted
const DELETE_DELAY = 150;

interface CommonFieldBuilderProps<
    F extends Field<FT, string>,
    FT extends { type: FTN },
    FTN extends string,
> {
    renderField: (
        fieldAtom: FormAtom<F>,
        openDrawer: () => void
    ) => React.ReactElement;
    renderFieldDrawerHeader: (fieldAtom: FormAtom<F>) => React.ReactNode;
    renderFieldDrawerCustomSection: (
        fieldAtom: FormAtom<F>,
        ref: CompoundFormForwardedRef
    ) => React.ReactNode;
    getFieldTypeConditionalValues: (fieldType: FT) => Array<string>;
    getFieldTypeDrawerBehavior: (fieldType: FT) => DrawerBehavior;
    disabled?: boolean;
    createNewField: <FN extends FT["type"] | FTN>(
        fieldTypeName: FN,
        fieldType?: FT
    ) => F;
    blobsAtom: FormAtom<SavedBlob[]>;
}
export interface FieldBuilderProps<
    F extends Field<FT, string>,
    FT extends { type: FTN },
    FTN extends string,
> extends CommonFieldBuilderProps<F, FT, FTN> {
    contentLabel: string;
    buttonPanelLabel: string;
    headerAction?: React.ReactNode;
    fieldButtons: Array<Omit<ElementButtonSpec<FTN>, "onClick">>;
    fieldsAtom: FormAtom<Array<F>>;
    renderFieldPlaceholder: (fieldAtom: FormAtom<F>) => React.ReactElement;
    noElementsLabel?: string;
}

export type FieldDependencyLookupResult<F> =
    | {
          type: "FOUND";
          value: F;
      }
    | {
          type: "FOUND_AFTER";
          value: F;
      }
    | {
          type: "NOT_FOUND";
      };

function useFieldsAtomFields<
    F extends Field<FT, string>,
    FT extends { type: FTN },
    FTN extends string,
>(fieldsAtom: FormAtom<Array<F>>) {
    return useMemo(() => {
        const fieldsAtomsAtom = insertableSplitFormAtom(
            fieldsAtom,
            // Allow fields to be "move"d
            (field) => field.id
        );
        return {
            fieldsAtomsAtom,
            sectionSpecsAtom: createAtom((get) => [get(fieldsAtomsAtom)]),
        };
    }, [fieldsAtom]);
}

function FieldBuilderInner<
    F extends Field<FT, string>,
    FT extends { type: FTN },
    FTN extends string,
>(
    {
        contentLabel,
        buttonPanelLabel,
        headerAction,
        fieldButtons,
        fieldsAtom,
        renderField,
        renderFieldPlaceholder,
        renderFieldDrawerHeader,
        renderFieldDrawerCustomSection,
        getFieldTypeConditionalValues,
        getFieldTypeDrawerBehavior,
        blobsAtom,
        noElementsLabel,
        createNewField,
        disabled = false,
    }: FieldBuilderProps<F, FT, FTN>,
    ref: CompoundFormForwardedRef
) {
    const { fieldsAtomsAtom, sectionSpecsAtom } =
        useFieldsAtomFields(fieldsAtom);

    const sectionSpecs = useAtomValue(sectionSpecsAtom);

    const allAtoms = useMemo(() => {
        return {
            fieldsAtomsAtom,
        };
    }, [fieldsAtomsAtom]);
    const { validate: validateForm } = useCompoundFormState(
        allAtoms,
        fieldsAtom
    );
    useImperativeHandle(ref, () => {
        return {
            validate(force) {
                return validateForm(force);
            },
        };
    }, [validateForm]);

    const [fieldsAtoms, setFieldsAtoms] = useAtom(fieldsAtomsAtom);
    const [blobsAtomValue, setBlobsAtomValue] = useAtom(blobsAtom);

    const [openDrawerFieldId, setOpenDrawerFieldId] = useState<string | null>(
        null
    );

    const getField = useAtomCallback(
        useCallback((get, _set, fieldAtom: FormAtom<F>) => {
            return get(fieldAtom);
        }, [])
    );

    let buttonSpecs: Array<ElementButtonSpec<FTN>>;
    let buttonsBehaviour: ButtonsBehaviour<FTN>;
    let elementsAndSectionsBehaviour: ElementsAndSectionsBehaviour;

    const buildEditButtonSpecs = useCallback(
        (onButtonClick: (fieldTypeName: FTN) => void) => {
            return fieldButtons.map((button) =>
                set(button as ElementButtonSpec<FTN>, {
                    onClick: () => onButtonClick(button.id),
                })
            );
        },
        [fieldButtons]
    );

    switch (disabled) {
        case true: {
            buttonSpecs = fieldButtons;
            buttonsBehaviour = {
                type: "HIDDEN",
            };
            elementsAndSectionsBehaviour = {
                type: "FIXED",
            };
            break;
        }
        case false: {
            const onAddButton = (
                fieldTypeName: FTN,
                _: SectionSpecId,
                location: GridLocation
            ) => {
                const newField = createNewField(fieldTypeName);
                if (fieldsAtoms.length <= location.row) {
                    setFieldsAtoms({ type: "insert", value: newField });
                } else {
                    setFieldsAtoms({
                        type: "insert",
                        value: newField,
                        before: fieldsAtoms[location.row],
                    });
                }
            };
            buttonSpecs = buildEditButtonSpecs((fieldTypeName: FTN) =>
                onAddButton(
                    fieldTypeName,
                    "00000000-0000-0000-0000-000000000000",
                    { row: fieldsAtoms.length, column: 0 }
                )
            );
            buttonsBehaviour = {
                type: "DRAGGABLE",
                onAddButton,
            };
            elementsAndSectionsBehaviour = {
                type: "DRAGGABLE_ELEMENTS",
                onMoveElement(sourceElementId, sourcePosition, targetPosition) {
                    const sourceAtom = fieldsAtoms[sourcePosition.location.row];
                    if (!sourceAtom) {
                        throw new Error("No source element at this location");
                    }
                    const sourceField = getField(sourceAtom);
                    if (sourceField.id !== sourceElementId) {
                        throw new Error("Wrong source element id");
                    }

                    const beforePosition =
                        targetPosition.location.row <
                        sourcePosition.location.row
                            ? targetPosition.location.row
                            : targetPosition.location.row + 1;
                    const beforeAtom = fieldsAtoms[beforePosition];

                    setFieldsAtoms({
                        type: "move",
                        atom: sourceAtom,
                        before: beforeAtom,
                    });
                },
            };
            break;
        }
        default: {
            return assertNever(disabled);
        }
    }

    return (
        <>
            <ElementDesigner<Array<FormAtom<F>>, FormAtom<F>, FTN>
                // Required because <ElementDesigner> does not support a switch of buttonsBehaviour.type out of the box
                key={buttonsBehaviour.type}
                className={theme.designer}
                contentClassName={theme.designerContent}
                sectionsClassName={theme.designerSections}
                contentTitle={contentLabel}
                buttonPanelTitle={buttonPanelLabel}
                headerAction={headerAction}
                buttonSpecs={buttonSpecs}
                sectionSpecs={sectionSpecs}
                getSectionId={() => "00000000-0000-0000-0000-000000000000"}
                getColCount={() => 1}
                getElementColumns={(section) => [section]}
                getElementId={(fieldAtom) => getField(fieldAtom).id}
                renderElement={(fieldAtom) => (
                    <RenderedField<F, FT, FTN>
                        renderField={renderField}
                        fieldAtom={fieldAtom}
                        disabled={disabled}
                        fieldAtoms={sectionSpecs}
                        createNewField={createNewField}
                        renderFieldDrawerHeader={renderFieldDrawerHeader}
                        renderFieldDrawerCustomSection={
                            renderFieldDrawerCustomSection
                        }
                        getFieldTypeConditionalValues={
                            getFieldTypeConditionalValues
                        }
                        getFieldTypeDrawerBehavior={getFieldTypeDrawerBehavior}
                        getField={getField}
                        blobsAtom={blobsAtom}
                        blobsAtomValue={blobsAtomValue}
                        setBlobsAtomValue={(value) =>
                            void setBlobsAtomValue(value)
                        }
                        setFieldsAtoms={setFieldsAtoms}
                        fieldsAtoms={fieldsAtoms}
                        setOpenDrawerFieldId={setOpenDrawerFieldId}
                        fieldId={getField(fieldAtom).id}
                        open={openDrawerFieldId === getField(fieldAtom).id}
                    />
                )}
                renderElementPlaceholder={renderFieldPlaceholder}
                buttonsBehaviour={buttonsBehaviour}
                elementsAndSectionsBehaviour={elementsAndSectionsBehaviour}
                noElementsLabel={noElementsLabel}
            />
        </>
    );
}

interface RenderedFieldProps<
    F extends Field<FT, string>,
    FT extends { type: FTN },
    FTN extends string,
> extends CommonFieldBuilderProps<F, FT, FTN> {
    fieldAtom: FormAtom<F>;
    fieldAtoms: FormAtom<F>[][];
    blobsAtomValue: SavedBlob[];
    setBlobsAtomValue: (value: SavedBlob[]) => void;
    setFieldsAtoms: (value: SplitFormAtomAction<F>) => void;
    fieldsAtoms: FormAtom<F>[];
    setOpenDrawerFieldId: (fieldId: string | null) => void;
    getField: (fieldAtom: FormAtom<F>) => F;
    fieldId: string;
    open: boolean;
}
function RenderedFieldNoMemo<
    F extends Field<FT, string>,
    FT extends { type: FTN },
    FTN extends string,
>({
    fieldAtom,
    disabled,
    renderField,
    fieldAtoms,
    renderFieldDrawerHeader,
    renderFieldDrawerCustomSection,
    getFieldTypeConditionalValues,
    getFieldTypeDrawerBehavior,
    blobsAtom,
    getField,
    fieldId,
    createNewField,
    blobsAtomValue,
    setBlobsAtomValue,
    setFieldsAtoms,
    fieldsAtoms,
    setOpenDrawerFieldId,
    open,
}: RenderedFieldProps<F, FT, FTN>) {
    const field = useAtomValue(fieldAtom);

    const { mutateAsync: copyBlobs } = useCopyBlobsMutation();

    const type: FieldDrawerType<F, FT, FTN> = useMemo(
        () =>
            disabled
                ? { type: "READ_ONLY" }
                : {
                      type: "EDIT",
                      onDeleteField: () => {
                          const baseFieldAtomIndex = fieldsAtoms.findIndex(
                              (fieldAtom) => getField(fieldAtom).id === fieldId
                          );
                          if (baseFieldAtomIndex >= 0) {
                              setOpenDrawerFieldId(null);
                              setTimeout(() => {
                                  setFieldsAtoms({
                                      type: "remove",
                                      atom: fieldsAtoms[baseFieldAtomIndex],
                                  });
                              }, DELETE_DELAY);
                          }
                      },
                      onCopyField: async (oldFieldAtom: FormAtom<F>) => {
                          const field = getField(oldFieldAtom);
                          const newField = createNewField(
                              field.fieldType.type,
                              field.fieldType
                          );
                          newField.title = field.title;
                          newField.instructions = field.instructions;
                          newField.mandatory = field.mandatory;
                          newField.condition = field.condition;

                          if (field.blobIds.length > 0) {
                              const response = await copyBlobs({
                                  data: field.blobIds,
                              });
                              const copiedBlobs = response.data;
                              newField.blobIds = copiedBlobs.map(
                                  (blob) => blob.id
                              );
                              const missingBlobs = copiedBlobs.filter(
                                  (blob) =>
                                      !blobsAtomValue.find(
                                          (bl) => bl.id === blob.id
                                      )
                              );
                              setBlobsAtomValue([
                                  ...blobsAtomValue,
                                  ...missingBlobs,
                              ]);
                          }
                          setOpenDrawerFieldId(newField.id);
                          setFieldsAtoms({ type: "insert", value: newField });
                      },
                  },
        [
            disabled,
            fieldsAtoms,
            getField,
            setFieldsAtoms,
            setOpenDrawerFieldId,
            createNewField,
            copyBlobs,
            blobsAtomValue,
            setBlobsAtomValue,
            fieldId,
        ]
    );

    return (
        <>
            <FieldDrawer
                type={type}
                open={open}
                state={{ fieldAtom }}
                fieldAtoms={fieldAtoms[0]}
                renderFieldDrawerHeader={renderFieldDrawerHeader}
                renderFieldDrawerCustomSection={renderFieldDrawerCustomSection}
                getFieldTypeConditionalValues={getFieldTypeConditionalValues}
                getFieldTypeDrawerBehavior={getFieldTypeDrawerBehavior}
                onClose={() => setOpenDrawerFieldId(null)}
                disabled={disabled}
                blobsAtom={blobsAtom}
            />
            {renderField(fieldAtom, () => {
                setOpenDrawerFieldId(field.id);
            })}
        </>
    );
}

const RenderedField = memo(RenderedFieldNoMemo, (prevProps, nextProps) => {
    const areEq =
        prevProps.disabled === nextProps.disabled &&
        prevProps.fieldId === nextProps.fieldId &&
        prevProps.open === nextProps.open;
    return areEq;
}) as typeof RenderedFieldNoMemo;

export const FieldBuilder = React.forwardRef(FieldBuilderInner);
